import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Grid,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
} from "@mui/material";
import { Search as SearchIcon, Close as CloseIcon } from "@mui/icons-material";

const ProductPickerModal = ({
  open,
  onClose,
  onSelect,
  selectedProductIds = [],
  limit = 20,
}) => {
  const API_BASE_URL =
    process.env.REACT_APP_API_URL || "https://admin-api.printformee.com/api";

  const [products, setProducts] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedIds, setSelectedIds] = useState(selectedProductIds || []);

  // Helper function to extract category ID flexibly
  const getProductCategoryId = (product) => {
    if (!product) return "";

    // If category is a string ID
    if (typeof product.category === "string") {
      return product.category;
    }

    // If category is an object with _id
    if (product.category?._id) {
      return product.category._id;
    }

    // If category is an object with id
    if (product.category?.id) {
      return product.category.id;
    }

    // If there's a separate categoryId field
    if (product.categoryId) {
      return product.categoryId;
    }

    // If there's a category_id field
    if (product.category_id) {
      return product.category_id;
    }

    return "";
  };

  // Fetch products with search, filters, and pagination
  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      // Debug: Log selected category
      console.log("🔍 Selected category filter:", category);

      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (category) params.append("category_id", category);
      if (status) params.append("status", status);
      params.append("page", page);
      params.append("limit", limit);

      const response = await axios.get(
        `${API_BASE_URL}/products/public?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const productsData = response.data.data || response.data.products || [];
      const total = response.data.total || productsData.length;

      // Debug: Log product category data structure
      if (productsData.length > 0) {
        console.log("📦 Sample product full data:", productsData[0]);
        console.log("📦 Sample product category field:", productsData[0]?.category);
        console.log("📦 Extracted category ID:", getProductCategoryId(productsData[0]));
      }

      const mappedProducts = productsData.map((p) => ({
        id: p._id || p.id,
        name: p.name || p.product_name_en || p.displayName,
        price: p.price || p.product_price || 0,
        image: p.images?.[0]?.url || p.image || p.thumbnail || "/placeholder.png",
        category_id: getProductCategoryId(p),
        category: p.category, // Keep original category for filtering
        categoryId: p.categoryId,
        status: p.status || "active",
      }));

      setProducts(mappedProducts);
      setTotalProducts(total);
    } catch (error) {
      console.error("Error fetching products:", error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [search, page, category, status, limit, API_BASE_URL]);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_BASE_URL}/categories`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // Handle different API response structures
        const categoriesData =
          response.data.categories ||
          response.data.data ||
          response.data ||
          [];
        const categoriesArray = Array.isArray(categoriesData) ? categoriesData : [];
        setCategories(
          categoriesArray.map((c) => ({
            id: c._id || c.id,
            name: c.name || c.category_name,
          }))
        );
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    if (open) {
      fetchCategories();
    }
  }, [open, API_BASE_URL]);

  // Fetch products on search, filter, or page change
  useEffect(() => {
    if (open) {
      fetchProducts();
    }
  }, [open, search, category, status, page, fetchProducts]);

  // Handle checkbox toggle
  const handleToggleProduct = (productId) => {
    setSelectedIds((prevIds) =>
      prevIds.includes(productId)
        ? prevIds.filter((id) => id !== productId)
        : [...prevIds, productId]
    );
  };

  // Handle chip remove
  const handleRemoveProduct = (productId) => {
    setSelectedIds((prevIds) =>
      prevIds.filter((id) => id !== productId)
    );
  };

  // Handle confirm selection
  const handleConfirm = () => {
    onSelect(selectedIds);
    onClose();
  };

  // Handle search with debounce
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1); // Reset to first page on search
  };

  const totalPages = Math.ceil(totalProducts / limit);
  const selectedProducts = products.filter((p) => selectedIds.includes(p.id));
  
  // Filter products for display
  const filteredProducts = products.filter((product) => {
    // Match category if selected
    const matchesCategory =
      !category || getProductCategoryId(product) === category;

    // Match status if selected
    const matchesStatus =
      !status || product.status === status;

    return matchesCategory && matchesStatus;
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        Select Products
        {selectedIds.length > 0 && (
          <Typography variant="caption" sx={{ ml: 2, color: "#666" }}>
            ({selectedIds.length} selected)
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        {/* Search Input */}
        <TextField
          fullWidth
          placeholder="Search product name..."
          value={search}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: "#999" }} />,
          }}
          sx={{ mb: 2 }}
          size="small"
        />

        {/* Filters */}
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 2 }}>
          <FormControl size="small">
            <InputLabel>Category</InputLabel>
            <Select
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                setPage(1);
              }}
              label="Category"
            >
              <MenuItem value="">All Categories</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
              label="Status"
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Selected Products Chips */}
        {selectedIds.length > 0 && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: "#f5f5f5", borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Selected Products:
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {selectedProducts.map((product) => (
                <Chip
                  key={product.id || product._id}
                  label={product.name}
                  onDelete={() => handleRemoveProduct(product.id)}
                  size="small"
                  sx={{
                    backgroundColor: "#e3f2fd",
                    color: "#1976d2",
                    fontWeight: 500,
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Products Grid */}
        <Box sx={{ mb: 2 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress />
            </Box>
          ) : filteredProducts.length === 0 ? (
            <Typography sx={{ textAlign: "center", py: 3, color: "#999" }}>
              No products found
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {filteredProducts.map((product, index) => (
                <Grid item xs={12} key={product.id || product._id || index}>
                  <Card
                    sx={{
                      p: 1.5,
                      cursor: "pointer",
                      border: selectedIds.includes(product.id)
                        ? "2px solid #1976d2"
                        : "1px solid #ddd",
                      backgroundColor: selectedIds.includes(product.id)
                        ? "#f5f5f5"
                        : "transparent",
                      transition: "all 0.2s",
                      "&:hover": { boxShadow: 2 },
                    }}
                    onClick={() => handleToggleProduct(product.id)}
                  >
                    <CardContent sx={{ p: 0, display: "flex", gap: 2, alignItems: "center" }}>
                      <Checkbox
                        checked={selectedIds.includes(product.id)}
                        onChange={() => handleToggleProduct(product.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <img
                        src={product.image}
                        alt={product.name}
                        style={{
                          width: 60,
                          height: 60,
                          borderRadius: 4,
                          objectFit: "cover",
                        }}
                        onError={(e) => {
                          e.target.src = "/placeholder.png";
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                        >
                          {product.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#666" }}>
                          ₹{product.price?.toFixed(2) || "0.00"}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

        {/* Pagination Info */}
        {!loading && filteredProducts.length > 0 && (
          <Typography variant="caption" sx={{ color: "#666" }}>
            Showing {(page - 1) * limit + 1} to{" "}
            {Math.min(page * limit, filteredProducts.length)} of {filteredProducts.length} products
          </Typography>
        )}
      </DialogContent>

      {/* Pagination Controls */}
      <DialogActions sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
        <Box>
          <Button
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            size="small"
          >
            Previous
          </Button>
          <Typography variant="caption" sx={{ mx: 1 }}>
            Page {page} of {totalPages}
          </Typography>
          <Button
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
            size="small"
          >
            Next
          </Button>
        </Box>
        <Box>
          <Button onClick={onClose} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleConfirm} variant="contained" sx={{ ml: 1 }}>
            Confirm ({selectedIds.length})
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ProductPickerModal;
